import React, { useEffect, useState } from "react";
import "../styles/agents.css";
import {
  Plus, Search, Building, Phone, Mail, MapPin,
  User, Edit, Trash2, Filter, AlertCircle,
  Hash, CreditCard, CheckCircle, Briefcase,
} from "lucide-react";
import CreateAgentDialog from "../components/agents/CreateAgentDialog";
import axios from "axios";

const BASE_URL = "https://crmapi.zauntrip.com";

export default function Agents() {
  const [agents, setAgents]               = useState([]);
  const [showCreateDialog, setShowCreate] = useState(false);
  const [showEditDialog, setShowEdit]     = useState(false);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [searchTerm, setSearchTerm]       = useState("");
  const [filterActive, setFilterActive]   = useState("all");
  const [loading, setLoading]             = useState(true);
  const [error, setError]                 = useState(null);
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => { fetchAgents(); }, []);

  const fetchAgents = async () => {
    try {
      setLoading(true); setError(null);
      const res = await axios.get(`${BASE_URL}/api/agents`);
      if (res.data.success) setAgents(res.data.data || []);
      else throw new Error(res.data.message);
    } catch (err) {
      setError(err.response?.data?.message || err.message || "Failed to load agents");
    } finally {
      setLoading(false);
    }
  };

  const handleCreateAgent = async (data) => {
    try {
      setActionLoading(true);
      const res = await axios.post(`${BASE_URL}/api/agents`, data);
      if (res.data.success) { setShowCreate(false); fetchAgents(); }
      else throw new Error(res.data.message);
    } catch (err) {
      alert(`Error: ${err.response?.data?.message || err.message}`);
      return Promise.reject(err);
    } finally { setActionLoading(false); }
  };

  const handleUpdateAgent = async (data) => {
    try {
      setActionLoading(true);
      const res = await axios.put(`${BASE_URL}/api/agents/${selectedAgent._id}`, data);
      if (res.data.success) { setShowEdit(false); setSelectedAgent(null); fetchAgents(); }
      else throw new Error(res.data.message);
    } catch (err) {
      alert(`Error: ${err.response?.data?.message || err.message}`);
      return Promise.reject(err);
    } finally { setActionLoading(false); }
  };

  const handleToggleActive = async (agent) => {
    const action   = agent.isActive ? "deactivate" : "activate";
    const endpoint = agent.isActive
      ? `${BASE_URL}/api/agents/${agent._id}`          // DELETE = soft deactivate
      : `${BASE_URL}/api/agents/${agent._id}/activate`; // PATCH = activate

    if (!window.confirm(`Are you sure you want to ${action} this agent?`)) return;
    try {
      setActionLoading(true);
      const res = agent.isActive
        ? await axios.delete(endpoint)
        : await axios.patch(endpoint);
      if (res.data.success) fetchAgents();
      else throw new Error(res.data.message);
    } catch (err) {
      alert(`Error: ${err.response?.data?.message || err.message}`);
    } finally { setActionLoading(false); }
  };

  const filtered = agents.filter((a) => {
    const name = `${a.contactPersonName || ""} ${a.tradeName || ""}`.toLowerCase();
    const matchSearch =
      name.includes(searchTerm.toLowerCase()) ||
      a.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      a.contactNumber?.includes(searchTerm) ||
      a.travelAgencyRegistrationNumber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      a.membershipNumber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      a.agentId?.toLowerCase().includes(searchTerm.toLowerCase());

    const matchStatus =
      filterActive === "all" ||
      (filterActive === "active" && a.isActive) ||
      (filterActive === "inactive" && !a.isActive);

    return matchSearch && matchStatus;
  });

  const getAddress = (a) => {
    if (!a.address) return null;
    return [a.address.addressLine1, a.address.city, a.address.stateProvince, a.address.pinCode, a.address.country]
      .filter(Boolean).join(", ");
  };

  return (
    <div className="agents-page">
      {/* ── Header ── */}
      <div className="agents-header">
        <div>
          <h1>Agents</h1>
          <p>Manage travel agents and agencies</p>
        </div>
        <button className="btn-primary" onClick={() => setShowCreate(true)} disabled={actionLoading}>
          <Plus size={16} /> Add Agent
        </button>
      </div>

      {/* ── Filters ── */}
      <div className="agents-filters">
        <div className="search-box">
          <Search size={16} />
          <input
            placeholder="Search by name, email, agent ID, membership no..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="filter-group">
          <Filter size={16} />
          <select value={filterActive} onChange={(e) => setFilterActive(e.target.value)}>
            <option value="all">All Status</option>
            <option value="active">Active Only</option>
            <option value="inactive">Inactive Only</option>
          </select>
        </div>
      </div>

      {/* ── Stats ── */}
      <div className="agents-stats">
        <div className="stat-card"><h3>{agents.filter((a) => a.isActive).length}</h3><p>Active Agents</p></div>
        <div className="stat-card"><h3>{agents.filter((a) => a.membershipNumber).length}</h3><p>With Membership</p></div>
        <div className="stat-card"><h3>{agents.length}</h3><p>Total Agents</p></div>
      </div>

      {/* ── States ── */}
      {loading && <div className="loading-state"><p>Loading agents...</p></div>}

      {error && !loading && (
        <div className="error-state">
          <AlertCircle size={24} />
          <p>{error}</p>
          <button onClick={fetchAgents} className="btn-outline" disabled={actionLoading}>Retry</button>
        </div>
      )}

      {/* ── Grid ── */}
      {!loading && !error && (
        <div className="agents-grid">
          {filtered.map((agent) => (
            <div key={agent._id} className="agent-card">
              {/* Card header */}
              <div className="agent-header">
                <div className="agent-avatar">
                  {agent.agentType === "Firm" ? <Building size={22} /> : <User size={22} />}
                </div>
                <div className="agent-info">
                  <h3>
                    {agent.contactPersonName || "—"}
                    {agent.tradeName && <span style={{ fontSize: 13, color: "#6b7280", marginLeft: 6 }}>({agent.tradeName})</span>}
                  </h3>
                  <div className="agent-meta">
                    <span className="badge badge-outline">{agent.agentType || "Individual"}</span>
                    {agent.agentId && <span className="badge badge-primary">{agent.agentId}</span>}
                    {!agent.isActive && <span className="badge badge-danger">Inactive</span>}
                  </div>
                </div>
              </div>

              {/* Card body */}
              <div className="agent-details">
                {/* Contact */}
                <div className="detail-group">
                  {agent.designation && <p><User size={13} /> {agent.designation}</p>}
                  {agent.email && <p><Mail size={13} /><a href={`mailto:${agent.email}`}>{agent.email}</a></p>}
                  {agent.contactNumber && <p><Phone size={13} /><a href={`tel:${agent.contactNumber}`}>{agent.contactNumber}</a></p>}
                  {agent.whatsappNumber && agent.whatsappNumber !== agent.contactNumber && (
                    <p><Phone size={13} /> WhatsApp: {agent.whatsappNumber}</p>
                  )}
                  {getAddress(agent) && <p className="address"><MapPin size={13} /><span>{getAddress(agent)}</span></p>}
                </div>

                {/* Business */}
                {(agent.panNumber || agent.gstNumber || agent.membershipNumber || agent.travelAgencyRegistrationNumber) && (
                  <div className="detail-group">
                    {agent.businessType && <p><Briefcase size={13} /> {agent.businessType}</p>}
                    {agent.panNumber    && <p><Hash size={13} /> PAN: {agent.panNumber}</p>}
                    {agent.gstNumber    && <p><Hash size={13} /> GST: {agent.gstNumber}</p>}
                    {agent.membershipNumber && (
                      <p><CheckCircle size={13} /> {agent.membershipType || "Membership"}: {agent.membershipNumber}</p>
                    )}
                    {agent.travelAgencyRegistrationNumber && (
                      <p><Hash size={13} /> Reg: {agent.travelAgencyRegistrationNumber}</p>
                    )}
                  </div>
                )}

                {/* Bank */}
                {agent.bankName && (
                  <div className="detail-group">
                    <p><CreditCard size={13} /> {agent.bankName}{agent.branchName ? ` — ${agent.branchName}` : ""}</p>
                    {agent.accountNumber && <p style={{ fontSize: 12, color: "#6b7280", paddingLeft: 21 }}>A/C: ••••{agent.accountNumber.slice(-4)}</p>}
                  </div>
                )}
              </div>

              {/* Actions */}
              <div className="agent-actions">
                <button
                  className="btn-outline btn-sm"
                  onClick={() => { setSelectedAgent(agent); setShowEdit(true); }}
                  disabled={actionLoading}
                >
                  <Edit size={13} /> Edit
                </button>
                <button
                  className={agent.isActive ? "btn-danger btn-sm" : "btn-success btn-sm"}
                  onClick={() => handleToggleActive(agent)}
                  disabled={actionLoading}
                >
                  <Trash2 size={13} /> {agent.isActive ? "Deactivate" : "Activate"}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {!loading && !error && filtered.length === 0 && (
        <div className="empty-state">
          <User size={36} />
          <h3>No agents found</h3>
          <p>{searchTerm ? "Try a different search term" : "Add your first agent to get started"}</p>
          {!searchTerm && (
            <button className="btn-primary" onClick={() => setShowCreate(true)} disabled={actionLoading}>
              Add Agent
            </button>
          )}
        </div>
      )}

      {/* ── Dialogs ── */}
      <CreateAgentDialog
        open={showCreateDialog}
        onClose={() => setShowCreate(false)}
        onSubmit={handleCreateAgent}
        isLoading={actionLoading}
      />

      {selectedAgent && (
        <CreateAgentDialog
          open={showEditDialog}
          onClose={() => { setShowEdit(false); setSelectedAgent(null); }}
          onSubmit={handleUpdateAgent}
          initialData={selectedAgent}
          isEdit={true}
          isLoading={actionLoading}
        />
      )}
    </div>
  );
}